<template>
  <div class="edit-row">
    <div data-testid="staffing-edit-from">
      <label for="From">Start date *</label>
      <AlDatePicker
        v-model="startDate"
        name="From"
        :min="minDate"
        :max="maxDate"
      />
    </div>

    <div class="number-input" data-testid="staffing-edit-hires">
      <label>Number of Hires *</label>
      <AlInput
        v-model="hires"
        type="number"
        :min="1"
        :max="100"
        @input="validateHiresValue"
      />
    </div>

    <div data-testid="staffing-edit-template">
      <label>User Template *</label>
      <AlSelect
        v-model="template"
        :modelValue="template"
        :options="templateOptions"
        :searchable="true"
      ></AlSelect>
    </div>
    <div data-testid="staffing-edit-timezone">
      <label>Timezone *</label>
      <AlSelect
        v-model="timezone"
        :modelValue="timezone"
        :options="timezoneOptions"
        :searchable="true"
      ></AlSelect>
    </div>
    <div data-testid="staffing-edit-team">
      <label>Team *</label>
      <AlSelect
        v-model="team"
        :modelValue="team"
        :options="teamOptions"
        :searchable="true"
      ></AlSelect>
    </div>
    <div data-testid="staffing-edit-tags">
      <label>Tags</label>
      <AlSelect
        v-model="tags"
        :modelValue="tags"
        :options="tagOptions"
        track-by="id"
        :multiple="true"
        :close-on-select="false"
      ></AlSelect>
    </div>
    <div class="edit-actions">
      <AlButton
        @click="save"
        data-testid="save-row"
        id="staffing-edit-save-row"
        :disabled="!canSave"
        ><i class="far fa-check" @click="save"
      /></AlButton>
      <AlButton
        @click="cancel"
        variant="secondary"
        data-testid="delete-row"
        id="staffinge-edit-cancel-row"
        ><i class="far fa-times"
      /></AlButton>
    </div>
  </div>
</template>

<script>
import {
  AlDatePicker,
  Al2Button as AlButton,
  AlSelect,
  AlInput,
} from "@playvox/almendra-components";
import { format, startOfDay, startOfMonth, endOfMonth } from "date-fns";
import { utcToZonedTime, zonedTimeToUtc } from "date-fns-tz";
import { getTimezonesList } from "@agyletime/date-utils";

import { prop } from "ramda";

export default {
  name: "EditStaffingRow",
  components: {
    AlDatePicker,
    AlButton,
    AlSelect,
    AlInput,
  },
  props: {
    configuration: {
      type: Object,
      required: true,
    },
    isNew: {
      type: Boolean,
      default: false,
    },
    staffing: {
      type: Object,
    },
    templateOptions: {
      type: Array,
      required: true,
    },
    teamOptions: {
      type: Array,
      required: true,
    },
    tagOptions: {
      type: Array,
      required: true,
    },
  },
  data() {
    let startDate = utcToZonedTime(new Date(), "UTC");
    if (this.staffing?.startDate) {
      startDate = utcToZonedTime(this.staffing.startDate, "UTC");
    }

    const timezone = this.staffing?.timezone || this.configuration.timezone;

    return {
      tags: this.staffing?.tags.map((t) =>
        this.tagOptions.find((to) => to.id === t)
      ),
      team: this.mapValueToOption("_id", this.staffing?.team, this.teamOptions),
      timezone: this.getTimezoneOptionForString(timezone),
      template: this.mapValueToOption(
        "_id",
        this.staffing?.template,
        this.templateOptions
      ),
      hires: this.staffing?.hires || 0,
      startDate: this.formatDate(startDate),
    };
  },
  methods: {
    mapValueToOption(key = "_id", value, options = []) {
      const propGetter = prop(key);
      return options.find((o) => propGetter(o) === propGetter(value));
    },
    mapValuesToOptions(key = "_id", values = [], options = []) {
      return values.map((v) => this.mapValueToOption(key, v, options));
    },
    getTimezoneOptionForString(tz) {
      return this.getTimezones().find((option) => option.id === tz);
    },
    cancel() {
      this.$emit("cancel");
    },
    save() {
      if (!this.canSave) {
        // Prevents a race condition
        return;
      }
      const result = {
        startDate: zonedTimeToUtc(
          startOfDay(utcToZonedTime(new Date(this.startDate), "UTC")),
          "UTC"
        ),
        hires: parseInt(this.hires),
        template: this.template,
        timezone: this.timezone.id,
        team: this.team,
        tags: this.tags?.map((t) => t.id) || [],
      };
      this.$emit("save", result);
    },
    formatDate(value) {
      return format(value, "yyyy-MM-dd");
    },
    getTimezones() {
      const timezones = getTimezonesList().map((tz) => ({
        id: tz,
        name: tz,
      }));
      timezones.sort((a, b) => a.name.localeCompare(b.name));
      return timezones;
    },
    validateHiresValue() {
      if (this.hires <= 0) {
        this.hires = 1;
      }
      if (this.hires > 100) {
        this.hires = 100;
      }
    },
  },
  computed: {
    defaultDate() {
      const date = utcToZonedTime(new Date());
      return this.formatDate(date);
    },
    minDate() {
      const date = utcToZonedTime(this.configuration.startDate);
      return this.formatDate(startOfMonth(date));
    },
    maxDate() {
      const date = endOfMonth(utcToZonedTime(this.configuration.endDate));
      return this.formatDate(endOfMonth(date));
    },
    timezoneOptions() {
      return this.getTimezones();
    },
    canSave() {
      return (
        this.startDate &&
        this.hires &&
        this.template?._id &&
        this.timezone &&
        this.team?._id
      );
    },
  },
};
</script>

<style scoped>
.edit-row {
  display: flex;
  justify-content: space-between;
  flex-wrap: wrap;
  width: 100%;
  align-items: flex-end;
}

.number-input {
  max-width: 7em;
}

.edit-actions > :first-child {
  margin-right: 1rem;
}
</style>
